import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IServerSideGetRowsRequest } from 'ag-grid-community';

import {
  ReportsService,
  ReportMeta,
  EndpointFilterValue,
  ReportNameEnum,
  SsrTotalRows,
  TimeFrame,
  PeriodFrequency,
} from '../../services/reports.service';
import { CommonService, ReportDate } from '../../services/common.service';
import { DriverIndexComponent } from '../../dialogs/driver-index/driver-index.component';
import { DriverIndexClassicComponent } from 'src/app/dialogs/driver-index-classic/driver-index-classic.component';
import { ReportOption, ServerSideRenderingOptions } from 'src/app/model/reports';
import { MisContent, CustomContentService } from 'src/app/services/custom-content.service';
import { CustomContentAdminComponent } from 'src/app/dialogs/custom-content-admin/custom-content-admin.component';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';

interface SortAndFilterModel {
  filterModel?: any;
  groupKeys?: Array<any>;
  sortModel?: Array<any>;
}

import { CustomMoreDetailsSeverityComponent } from 'src/app/components/ag-grid-components/custom-more-details-severity/custom-more-details-severity.component';
import { PredefinedFiltersService } from 'src/app/services/predefined-filters.service';

@Component({
  selector: 'common-reports',
  templateUrl: './common-reports.component.html',
  styleUrls: ['./common-reports.component.scss'],
})
export class CommonReportsComponent implements OnDestroy {
  requestedReport: string = '';
  reportTitle: string = '';
  commonReportReady: boolean = false;
  commonRptPageChanged: boolean = false;
  multiRowSelection: boolean = true;
  primaryKey: string = 'pin';
  ssrOptions: ServerSideRenderingOptions = null;

  serverDataDownloadFailed: boolean = false;
  serverDataDownloadComplete: boolean = false;

  rptOptions: ReportOption = { reportEndpointName: '' };
  reportMeta: ReportMeta = {};
  rawReportsData: Array<any> = [];
  reportDate: Partial<ReportDate> = {};
  pinnedTopRowData: Array<any> = [];
  pinnedBottomRowData: Array<any> = [];

  canDrilldownToDriverPerformanceRecord: boolean = true;
  nonStandardDrilldownReports: Array<string> = [];

  private subscriptions = {
    reportMeta: null,
    serverFilter: null,
    dprDialogClosed: null,
    navigationEnd: null,
    serverSideSearch: null,
    misNotificationsReportChanged: null,
    reportDataChanged: null,
  };

  private currentReportEndpoint: string;
  private dialogComponent = DriverIndexComponent;
  private classicDialogComponent = DriverIndexClassicComponent;
  private customContentAdminDialog = CustomContentAdminComponent;

  constructor(
    private rptService: ReportsService,
    private activatedRoute: ActivatedRoute,
    private cService: CommonService,
    private translate: TranslateService,
    private cContentService: CustomContentService,
    private dialog: MatDialog,
    private predefinedFiltersService: PredefinedFiltersService
  ) {
    this.activatedRoute.params.subscribe({
      next: (param: { report: string }) => {
        this.commonReportReady = false;
        this.currentReportEndpoint = param.report.replace(/-/g, `/`);

        if (this.currentReportEndpoint) {
          this.requestedReport = this.currentReportEndpoint;
          this.processCommonRptChangedCache();
        } else {
          console.error('ERROR: Requested report endpoint not found');
        }

        this.rptOptions = {
          reportEndpointName: this.requestedReport,
          mobileColumns: rptService.commonReportOptions.mobileColumns,
          sideBar: {
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
              },
              {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
              },
              {
                id: 'customDetails',
                labelDefault: 'VRM Mentor',
                labelKey: 'customDetails',
                iconKey: 'details',
                toolPanel: 'customDetailsToolPaneComponent',
              },
            ],

            defaultToolPanel: 'customDetails',
            hiddenByDefault: false,
          },
        };

        this.renderReport();
      },
    });

    this.invokeChangeSubscriptions();
  }

  private invokeChangeSubscriptions() {
    this.subscriptions.serverFilter = this.rptService.serverFilterChanged$.subscribe({
      next: (isCommonReport: boolean) => {
        if (this.commonReportReady) {
          console.log('Server / Date Filter Data CHANGED! Is Common Report:', isCommonReport);
          this.renderReport();
        }
      },
    });

    this.subscriptions.dprDialogClosed = this.rptService.driverPerformanceDialogClosed$.subscribe({
      next: (filterValue: EndpointFilterValue) => {
        if (this.commonReportReady && (!this.ssrOptions || !this.ssrOptions.useServerSideRendering)) {
          this.renderReport();
        }
      },
    });

    this.subscriptions.reportDataChanged = this.rptService.reportDataChanged$.subscribe({
      next: (filterValue: EndpointFilterValue) => {
        if (this.commonReportReady && (!this.ssrOptions || !this.ssrOptions.useServerSideRendering)) {
          this.renderReport();
        }
      },
    });

    this.subscriptions.misNotificationsReportChanged = this.rptService.misNotificationsChanged$.subscribe({
      next: (filterValue: EndpointFilterValue) => {
        if (this.commonReportReady) {
          this.renderReport();
          console.log('::::: REPORT ENDPOINT FILTER Changed!', filterValue);
        }
      },
    });
  }

  private suppressUnusedColumnPaneFuncsTillPhaseTwo() {
    const suppressedPanels = {
      suppressRowGroups: true,
      suppressValues: true,
      suppressPivots: true,
      suppressPivotMode: true,
    };

    this.rptOptions.sideBar.toolPanels.find((panel) => panel['id'] === 'columns')['toolPanelParams'] = suppressedPanels;
  }

  private requestAndProcessReportData(
    usingAgGridServerSideRendering: boolean,
    successCallback: (reportResponse: any) => void,
    errorCallback: (error: any) => void
  ) {
    this.suppressUnusedColumnPaneFuncsTillPhaseTwo();

    if (usingAgGridServerSideRendering) {
      this.ssrOptions = {
        rowsPerPage: this.reportMeta.pagination.rowsPerPage,
        useServerSideRendering: true,
        dataSource: {
          getRows: (params: {
            request: any;
            success: (params: { rowData: Array<any>; rowCount: number }) => void;
            fail: () => void;
          }) => {
            if (this.cService.appInitialLoad) {
              let mergedRequestParams: IServerSideGetRowsRequest;
              const metaDataDefaults = this.convertMetaDefaultsToGridParams(this.reportMeta);
              mergedRequestParams = this.mergeGridStatePreferencesWithSsrRequestParams(
                params.request,
                metaDataDefaults
              );

              if (this.cService.userProfile.preferences && this.cService.userProfile.preferences.gridstate) {
                mergedRequestParams = this.mergeGridStatePreferencesWithSsrRequestParams(
                  params.request,
                  this.cService.userProfile.preferences.gridstate
                );
              }

              params.request = mergedRequestParams;
              console.log('%cMODIFIED Request Prams', 'lightblue', JSON.stringify(params.request, null, 1));
            }

            this.rptService.cachedAgGridServerParams = params.request;

            if (this.rptService.artificialSsrDataFeed) {
              const lastRow: number = this.rptService.ssrTotalRowsData.filtered
                ? this.rptService.ssrTotalRowsData.filtered
                : this.rptService.ssrTotalRowsData.total;

              console.log('%cArtificial Feed Hit', 'color: salmon', this.rptService.artificialSsrDataFeed);

              const successParams: { rowData: Array<any>; rowCount: number } = {
                rowData: this.rptService.artificialSsrDataFeed,
                rowCount: lastRow,
              };
              params.success(successParams);
            } else {
              this.rptService.commonReportSubscriptions.report = this.rptService
                .commonReport(this.currentReportEndpoint)
                .pipe(debounceTime(1000))
                .subscribe({
                  next: (resp: { data: Array<unknown>; pagination: SsrTotalRows }) => {
                    this.rptService.ssrTotalRowsData = resp.pagination;

                    let lastRow: number = resp.pagination.filtered ? resp.pagination.filtered : resp.pagination.total;

                    if (!resp.data.length) {
                      lastRow = 0;
                    }

                    const successParams: { rowData: Array<any>; rowCount: number } = {
                      rowData: resp.data,
                      rowCount: lastRow,
                    };
                    params.success(successParams);

                    if (successCallback) {
                      successCallback(resp);
                    }
                  },

                  error: (error: any) => {
                    params.fail();

                    if (errorCallback) {
                      errorCallback(error);
                    }
                  },
                });
            }
          },
        },
      };

      // NOTe: Since the grid has to call for data implicitly, we have to set ready true
      // prior to data calls, else we won't be able to handover request-code to the grid.
      this.commonReportReady = true;
    } else {
      this.rptService.commonReportSubscriptions.report = this.rptService
        .commonReport(this.currentReportEndpoint)
        .subscribe({
          next: (response: any) => {
            if (successCallback) {
              successCallback(response);
            }
          },

          error: (error) => {
            if (errorCallback) {
              errorCallback(error);
            }
          },
        });
    }
  }

  private mergeGridStatePreferencesWithSsrRequestParams(
    ssrRequest: IServerSideGetRowsRequest,
    columnDefaultState: SortAndFilterModel
  ): IServerSideGetRowsRequest {
    const mergedSsrRequest: IServerSideGetRowsRequest = ssrRequest;

    if (columnDefaultState) {
      const gridState = columnDefaultState;

      const gStateFilterModel = gridState.filterModel;
      const gStateSortModel: Array<any> = gridState.sortModel;
      const gStateGroupKeys: Array<any> = gridState.groupKeys;

      if (gStateFilterModel && Object.keys(gStateFilterModel).length) {
        mergedSsrRequest.filterModel = this.removeFiltersForNonExistentColumns({
          ...ssrRequest.filterModel,
          ...gStateFilterModel,
        });
      }

      if (gStateSortModel && gStateSortModel.length) {
        mergedSsrRequest.sortModel = [...ssrRequest.sortModel, ...gStateSortModel];
      }

      if (gStateGroupKeys && gStateGroupKeys.length) {
        mergedSsrRequest.groupKeys = [...ssrRequest.groupKeys, ...gStateGroupKeys];
      }
    }

    return mergedSsrRequest;
  }

  private removeFiltersForNonExistentColumns(filters: any) {
    const filteredFilters = {};

    Object.entries(filters).forEach(([filterColumn, filterData]) => {
      if (this.reportMeta.structure[filterColumn]) {
        filteredFilters[filterColumn] = filterData;
      }
    });

    return filteredFilters;
  }

  private convertMetaDefaultsToGridParams(metaData: ReportMeta): SortAndFilterModel {
    const collatedSortFilterModel: SortAndFilterModel = { sortModel: [], filterModel: {} };

    for (const structKey in metaData.structure) {
      if (structKey) {
        const columnStruct = metaData.structure[structKey];

        if (columnStruct) {
          if (columnStruct.defaultSort) {
            collatedSortFilterModel.sortModel.push({
              colId: structKey,
              sort: columnStruct.defaultSort.direction,
            });
          }

          if (columnStruct.default) {
            if (columnStruct.type === 'text') {
              collatedSortFilterModel.filterModel[structKey] = {
                filterType: 'set',
                values: Array.isArray(columnStruct.default) ? columnStruct.default : [columnStruct.default],
              };
            }
          }
        }
      }
    }

    return collatedSortFilterModel;
  }

  private renderReport() {
    this.resetCommonReportPage();

    this.pinnedTopRowData = [];
    this.pinnedBottomRowData = [];

    if (this.currentReportEndpoint) {
      this.reportTitle = `${this.cService.readyTranslations.messagesAndWarnings.working}...`;

      // ---- New implementation ----
      this.rptService.commonReportSubscriptions.meta = this.rptService
        .commonReportMeta(this.currentReportEndpoint)
        .subscribe({
          next: (response: ReportMeta) => {
            this.reportMeta = response;
            this.rptService.currentReportMeta = this.reportMeta;
            const usingSsr: boolean = this.reportMeta.usesServerSideRendering ? true : false;

            if (this.rptService.commonRptPageChangedOnLoad) {
              this.predefinedFiltersService.resetState();
              this.rptService.requestedServerFilters = {};
            }

            if (this.reportMeta.serverFilters && this.reportMeta.serverFilters.filterGroups) {
              this.predefinedFiltersService.setupNewFilters(this.reportMeta.serverFilters);
            }

            this.rptService.triggerBehaviourSubjectOnNewCommonReportsMeta({ reportMeta: this.reportMeta });

            this.requestAndProcessReportData(
              usingSsr,
              (reportResponse) => {
                this.rawReportsData = reportResponse.data;
                this.reportDate = reportResponse.date;

                this.cService.hideIndeterminateProgress = true;

                this.reportStructureSpecifiesDefaultClientFilter(this.reportMeta);

                this.cService.currentPageDateOrPeriod = {};

                if (reportResponse.date) {
                  this.cService.currentPageDateOrPeriod.date = reportResponse.date;
                } else if (reportResponse.dateComparison) {
                  this.cService.currentPageDateOrPeriod.dateComparison = reportResponse.dateComparison;
                }

                this.getCurrentCustomContentType();

                if (this.reportMeta.moreDetails) {
                  const customDetailsPanelConfigKey = this.rptOptions.sideBar.toolPanels.findIndex(
                    (toolPanel) => toolPanel['id'] === 'customDetails'
                  );

                  this.rptOptions.sideBar.toolPanels[customDetailsPanelConfigKey]['toolPanelParams'] = {
                    moreDetails: this.resolveCustomDetailsPaneMoreDetailsComponent(this.reportMeta.moreDetails.type),
                    hideRowDetail: this.reportMeta.moreDetails.hideRowDetails
                      ? this.reportMeta.moreDetails.hideRowDetails
                      : false,
                    apiService: null,
                  };
                }

                this.determineWhichReportCanDrilldownToDpr();

                if (this.reportMeta && this.reportMeta.name === ReportNameEnum.station) {
                  if (this.rawReportsData.length > 0) {
                    const rowToPin = reportResponse.data.find(
                      (data: any) =>
                        data.station.toLowerCase() === 'all stations' || data.station.toLowerCase() === 'total'
                    );

                    if (rowToPin) {
                      if (rowToPin.station.toLowerCase() === 'all stations') {
                        rowToPin.station = this.cService.readyTranslations.reportData.allStations;
                      }

                      this.pinnedTopRowData = [rowToPin];
                      this.rawReportsData = this.rawReportsData.filter((data) => data.station != rowToPin['station']);
                    }
                  }
                }

                if (reportResponse?.pinnedBottomRowData) {
                  const rowToPin = reportResponse.pinnedBottomRowData.data;
                  if (rowToPin) {
                    this.pinnedBottomRowData = rowToPin;
                  }
                }

                this.rptService.hasTimeFrameFilter = this.reportMeta.defaultPeriod ? true : false;

                this.processColumnGroupLabel();

                this.applyEndpointFilters();

                this.cService.identifySelectedNavAndParent();

                this.commonReportReady = true;
                this.reportTitle = this.cService.commonReportTitle;

                this.getDefaultTimeFrameSettings();

                this.triggerCommonReportsDataChangeObservers();

                this.setAvailableCalendarFrequencies();

                this.rptService.commonRptPageChangedOnLoad = false;
              },

              () => {
                this.cService.hideIndeterminateProgress = true;
                this.reportTitle = `:: ${this.translate.instant('REPORTS').requestedReportFailed}`;
                this.triggerCommonReportsDataChangeObservers();
              }
            );

            this.refreshMenuNotificationCount();
          },

          error: () => {
            this.cService.hideIndeterminateProgress = true;
            this.reportTitle = `:: ${this.translate.instant('REPORTS').requestedReportFailed}`;
            this.triggerCommonReportsDataChangeObservers();
          },
        });
    } else {
      console.error(':: ERROR - No endpoint provided');
    }
  }

  private triggerCommonReportsDataChangeObservers() {
    this.rptService.triggerBehaviourSubjectOnNewCommonReportsDataOnLoad({ reportMeta: this.reportMeta });
    this.rptService.triggerBehaviourSubjectOnNewCommonReportsData({ reportMeta: this.reportMeta });
  }

  private refreshMenuNotificationCount() {
    if (this.reportMeta.information?.refreshMenuNotificationCountType) {
      this.cService.triggerMenuBadgeCountUpdateObservable(
        this.reportMeta.information?.refreshMenuNotificationCountType
      );
    }
  }

  countColumnSpan(metaStructureArray: unknown[], columnGroupId: string): number {
    let totalCount = 0;

    metaStructureArray.forEach((column) => {
      if (column[1].columnGroupId === columnGroupId) {
        totalCount++;
      }
    });

    return totalCount;
  }

  makeSelectedRowEditableViaDialog($event: any) {
    const renderClassicOrModernDialog: any =
      this.cService.userProfile.companySettings &&
      this.cService.userProfile.companySettings.driverPerformanceClassic === 'TRUE'
        ? this.classicDialogComponent
        : this.dialogComponent;

    if (this.reportMeta.information?.encryptedPin) {
      $event.data.securePin = true;
    }

    if (this.reportMeta.information?.hidePinFromDpr) {
      $event.data.hidePin = true;
    }

    this.dialog
      .open(renderClassicOrModernDialog, $event)
      .afterClosed()
      .subscribe(() => {
        this.rptService.triggerDriverPerformanceDialogClosedObservable(this.rptService.endpointFilterValue, true);
      });
  }

  private processCommonRptChangedCache() {
    const initialCachedPage = localStorage.getItem(this.cService.localStorageKeys.cachedCommonReportPage);
    if (initialCachedPage !== this.requestedReport) {
      this.rptService.commonRptPageChangedOnLoad = true;
      localStorage.setItem(this.cService.localStorageKeys.cachedCommonReportPage, this.requestedReport);
    }
  }

  private removeCommonRptChangedCache() {
    localStorage.removeItem(this.cService.localStorageKeys.cachedCommonReportPage);
    this.requestedReport = null;
  }

  private processColumnGroupLabel() {
    const metaStructureArray = Object.entries(this.reportMeta.structure) as unknown[];
    const hasColumnGroupLabels = metaStructureArray.some((column) => column[1].hasOwnProperty('columnGroupId'));
    if (hasColumnGroupLabels) {
      const groupedColumns = [];
      const subHeadingRow = [];
      subHeadingRow['isSubHeadingRow'] = true;

      metaStructureArray.forEach((column) => {
        const columnName = column[0];
        const columnData = column[1];

        if (!columnData.hasOwnProperty('columnGroupId')) {
          subHeadingRow[columnName] = null;
        } else {
          const columnGroupId = columnData.columnGroupId;
          const colSpan = this.countColumnSpan(metaStructureArray, columnGroupId);

          this.translate.get('REPORTCOLUMNS').subscribe({
            next: (translation) => {
              const columnGroupLabelObject = this.reportMeta.information.columnGroupLabels.find((groupLabel) => {
                return groupLabel.id === columnGroupId;
              });
              const columnGroupLabel = translation[columnGroupLabelObject.label]
                ? translation[columnGroupLabelObject.label]
                : columnGroupLabelObject.label;

              if (!groupedColumns.includes(columnGroupId)) {
                subHeadingRow[columnName] = columnGroupLabel;
                groupedColumns.push(columnGroupId);

                this.reportMeta.structure[columnName].colSpan = (params) => {
                  return params.data && params.data[columnName] === columnGroupLabel ? colSpan : 1;
                };
              }
            },
          });
        }

        this.reportMeta.structure[columnName].cellStyle = (params) => {
          return params.data && params.data['isSubHeadingRow']
            ? {
                textAlign: 'center',
                backgroundColor: '#f5f7f7',
                borderRightColor: '#d2d2d2 !important',
              }
            : null;
        };
      });

      this.pinnedTopRowData = [subHeadingRow];
    }
  }

  private applyEndpointFilters() {
    // TODO: Check if endpointFilters are ever returned at any point from the backend and decide if refactoring is necessary
    if (this.reportMeta.endpointFilters) {
      this.translate.get('FILTERS').subscribe({
        next: (translation) => {
          if (this.reportMeta.endpointFilters.length > 1) {
            this.rptService.multipleEndpointFilters = this.reportMeta.endpointFilters;

            this.rptService.multipleEndpointFilters.forEach((endpointFilters) => {
              const filterName = endpointFilters.name;
              endpointFilters.translatedName = translation[filterName];

              // Reset selected value & apply translations
              endpointFilters.values.map((value) => {
                value.selected = false;
                value.translatedText = translation[value.text] ? translation[value.text] : value.text;
              });

              if (!this.rptService.endpointFilterValue) {
                const defaultValue = endpointFilters.default;
                endpointFilters.values.find((filter) => filter.value === defaultValue).selected = true;
              }
            });

            if (this.rptService.endpointFilterValue) {
              const filterValue = this.rptService.endpointFilterValue;

              this.rptService.multipleEndpointFilters
                .find((endpointFilter) => endpointFilter.name === filterValue.filterName)
                .values.find((filter) => filter.value === filterValue.value).selected = true;
            }
          } else {
            if (this.reportMeta.endpointFilters.length > 0) {
              this.rptService.singleEndpointFilters = this.reportMeta.endpointFilters[0];

              const filterName = this.rptService.singleEndpointFilters.name;
              this.rptService.singleEndpointFilters.translatedName = translation[filterName];

              // Reset selected value & apply translations
              this.rptService.singleEndpointFilters.values.map((value) => {
                value.selected = false;
                value.translatedText = translation[value.text] ? translation[value.text] : value.text;
              });

              if (this.rptService.endpointFilterValue) {
                const filterValue = this.rptService.endpointFilterValue;
                this.rptService.singleEndpointFilters.values.find(
                  (filter) => filter.value === filterValue.value
                ).selected = true;
              } else {
                const defaultValue = this.rptService.singleEndpointFilters.default;
                this.rptService.singleEndpointFilters.values.find((filter) => filter.value === defaultValue).selected =
                  true;
              }
            }
          }
        },
      });
    }
  }

  administerMisContentDialog($event: any) {
    this.cContentService.contentAdminDialogSettings.data = null;
    this.dialog
      .open(this.customContentAdminDialog, this.cContentService.contentAdminDialogSettings)
      .afterClosed()
      .subscribe(() => {
        this.rptService.triggerMisNotificationAdminDialogClosedObservable();
      });
  }

  private determineWhichReportCanDrilldownToDpr() {
    if (this.reportMeta.name) {
      this.canDrilldownToDriverPerformanceRecord = true;

      if (this.nonStandardDrilldownReports.includes(this.reportMeta.name)) {
        this.canDrilldownToDriverPerformanceRecord = false;
      }

      if (this.reportMeta.information && this.reportMeta.information.disableDPR) {
        this.canDrilldownToDriverPerformanceRecord = !this.reportMeta.information.disableDPR;
      }
    }
  }

  getCurrentCustomContentType() {
    this.cContentService.currentContentType = '';

    if (this.rptService.currentReportMeta.name) {
      if (
        Object.values(this.cContentService.contentTypes).includes(this.rptService.currentReportMeta.name.toUpperCase())
      ) {
        this.cContentService.currentContentType = this.rptService.currentReportMeta.name.toUpperCase() as MisContent;

        if (
          !this.cService.appInitialLoad &&
          this.cContentService.currentContentType == this.cContentService.contentTypes.tooltips
        ) {
          this.cContentService.getAndCacheTooltips(this.rawReportsData, true, () => {
            this.cContentService.assignApplicablePageTooltips();
          });
        }
      }
    }
  }

  private getDefaultTimeFrameSettings() {
    if (this.reportMeta.defaultPeriod) {
      if (!this.rptService.selectedTimeFrame.timeFrame) {
        this.rptService.selectedTimeFrame.timeFrame = this.reportMeta.defaultPeriod;
      }

      if (this.reportDate) {
        this.rptService.selectedTimeFrame.timeFrame.date = this.reportDate.startPeriod;
      }
      console.log('Selected Time-Frame', this.rptService.selectedTimeFrame.timeFrame);
    } else {
      this.rptService.selectedTimeFrame.timeFrame = null;
    }
  }

  /**
   * override default available frequencies
   * that is initialised in reports service
   * with the values from current meta.
   */
  private setAvailableCalendarFrequencies() {
    if (this.reportMeta && this.reportMeta.availablePeriods) {
      this.rptService.availableFrequencies = [];
      if (this.reportMeta.availablePeriods.includes('year')) {
        this.rptService.availableFrequencies.push({ label: <TimeFrame>'yearly', value: 'year' });
      }

      if (this.reportMeta.availablePeriods.includes('month')) {
        this.rptService.availableFrequencies.push({ label: <TimeFrame>'monthly', value: 'month' });
      }

      if (this.reportMeta.availablePeriods.includes('week')) {
        this.rptService.availableFrequencies.push({ label: <TimeFrame>'weekly', value: 'week' });
      }

      if (this.reportMeta.availablePeriods.includes('day')) {
        this.rptService.availableFrequencies.push({ label: <TimeFrame>'daily', value: 'day' });
      }
    } else {
      this.rptService.availableFrequencies = [
        { label: <TimeFrame>'monthly', value: 'month' },
        { label: <TimeFrame>'weekly', value: 'week' },
        { label: <TimeFrame>'daily', value: 'day' },
      ];
    }

    this.setDefaultPeriodIfSelectedNotAvailable();
  }

  /**
   * if selected timeframe when navigating from another calendar report
   * is not available in availableFrequencies of current report,
   * set to default.
   */
  private setDefaultPeriodIfSelectedNotAvailable() {
    if (this.reportMeta && this.rptService.selectedTimeFrame.timeFrame) {
      const availableFrequency = this.rptService.availableFrequencies.filter((periodFrequency: PeriodFrequency) => {
        return periodFrequency.value === this.rptService.selectedTimeFrame.timeFrame.period;
      });

      if (!availableFrequency.length) {
        this.rptService.selectedTimeFrame.timeFrame = this.reportMeta.defaultPeriod;
      }
    }
  }

  private resetCommonReportPage() {
    this.cService.releaseMultipleSubscriptions(this.rptService.commonReportSubscriptions);
    this.reportMeta = null;
    this.rawReportsData = [];
    this.ssrOptions = null;
  }

  private reportStructureSpecifiesDefaultClientFilter(metaData: ReportMeta) {
    this.rptService.metadataHasDefaultFilter = false;
    const structEntries = Object.entries(metaData.structure);

    for (const [key, item] of structEntries) {
      if (item['default']) {
        this.rptService.metadataHasDefaultFilter = true;
        break;
      }
    }
  }

  private resolveCustomDetailsPaneMoreDetailsComponent(moreDetailsComponentType: string): any {
    return moreDetailsComponentType === 'severity-details' ? CustomMoreDetailsSeverityComponent : null;
  }

  ngOnDestroy(): void {
    this.cService.releaseMultipleSubscriptions(this.subscriptions);
    this.rptService.hasTimeFrameFilter = false;
    this.removeCommonRptChangedCache();
  }
}
